import { inject, Injector } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import {
  DashboardService,
  MlaAuthService,
  OriginationTokenRefreshService,
  WorkflowStateService,
} from '@ld.consumer-portal/data-access';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import {
  AnalyticsService,
  AuthService,
  CiamAuthService,
  LoggingService,
  ServisBotService,
  UserIdleService,
} from '@ld.consumer-portal/utils';

export const updatePasswordGuard: CanActivateFn = async () => {
  const injector = inject(Injector);
  const authService = inject(AuthService);
  const userIdleService = inject(UserIdleService);
  const analyticsService = inject(AnalyticsService);
  const servisBotService = inject(ServisBotService);
  const dashboardService = inject(DashboardService);
  const mlaAuthService = inject(MlaAuthService);
  const originationTokenRefreshService = inject(OriginationTokenRefreshService);
  const workflowStateService = inject(WorkflowStateService);
  const env = inject(ENVIRONMENT);
  const logger = inject(LoggingService);
  const ciamAuthService = inject(CiamAuthService);

  let msalService: MsalService | null = null;
  try {
    msalService = injector.get(MsalService, null);
  } catch {
    // MsalService not available
  }

  if (env.appSettings.useCustomerIdentityAuth) {
    try {
      await ciamAuthService.redirectForPasswordChange();
      return false;
    } catch (error) {
      logger.logException(error, '[updatePasswordGuard] CIAM redirectForPasswordChange failed, proceeding with cleanup');
    }
  }

  try {
    await servisBotService.clear();
  } catch (error) {
    logger.logException(error, 'Failed to clear ServisBot during update-password');
  }

  document.body.classList.remove('servisbot-enabled');

  dashboardService.clearCache();

  userIdleService.stopWatching();
  originationTokenRefreshService.stopMonitoring();

  mlaAuthService.logout();

  workflowStateService.clearWorkflowContext();

  authService.clearAllTokens();

  sessionStorage.clear();

  clearUserLocalStorage();

  localStorage.removeItem('mock_mfa_remember_device');

  if (env.appSettings.useCustomerIdentityAuth) {
    clearCiamState(ciamAuthService, logger);
  }

  if (msalService) {
    clearMsalAccounts(msalService, logger);
  }

  analyticsService.trackLogout();

  if (!env.appSettings.useCustomerIdentityAuth) {
    const updatePasswordUrl = env.appSettings?.originationAuth?.updatePasswordB2CUrl;
    if (updatePasswordUrl) {
      window.location.href = updatePasswordUrl;
      return false;
    }
  }

  window.location.href = '/login';

  return false;
};

function clearUserLocalStorage(): void {
  const keysToRemove = [
    'loanNumber',
    'helocLoanNumber',
    'isHeloc',
    'selectedLoanNumber',
    'tabWatch',
    'PaperlessDisplayStatus',
    'IsReviewPopupDesplayed',
    'ContactInfoConfirm',
    'isServisBotVisible',
  ];

  keysToRemove.forEach((key) => {
    localStorage.removeItem(key);
  });
}

function clearCiamState(ciamAuthService: CiamAuthService, logger: LoggingService): void {
  try {
    ciamAuthService.reset();
    localStorage.removeItem('ciam_session_active');
  } catch (error) {
    logger.logException(error, '[updatePasswordGuard] Failed to clear CIAM state');
  }
}

function clearMsalAccounts(msalService: MsalService, logger: LoggingService): void {
  try {
    msalService.instance.setActiveAccount(null);

    msalService.instance.clearCache();
  } catch (error) {
    logger.logException(error, '[updatePasswordGuard] Failed to clear MSAL accounts');
  }
}
